import { useEffect, useRef, useState } from 'react';
import { axios } from '../App';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Activity, Radio, Waves, Eye, Thermometer, Power, ExternalLink,
  Cpu, Target, Crosshair
} from 'lucide-react';

const SENSORS = [
  { id: 'RADAR-01', type: 'radar', label: 'Radar', icon: Radio,       color: '#22c55e' },
  { id: 'SONAR-01', type: 'sonar', label: 'Sonar', icon: Waves,       color: '#06b6d4' },
  { id: 'EO-01',    type: 'eo',    label: 'EO',    icon: Eye,         color: '#a855f7' },
  { id: 'IR-01',    type: 'ir',    label: 'IR',    icon: Thermometer, color: '#f97316' },
];

// Synthetic targets the demo simulator drives forward each tick.
// Each target is visible to a subset of sensors, so the fusion engine has
// real overlap to demonstrate.
function makeDemoTargets() {
  return [
    { name: 'Approaching FPV',     domain: 'air',        lat: 17.706, lng: 83.183, alt: 80,  spd: 90,  hdg: 130, sensors: ['radar', 'eo', 'ir'] },
    { name: 'Recon Quad',          domain: 'air',        lat: 17.726, lng: 83.243, alt: 120, spd: 60,  hdg: 215, sensors: ['radar', 'eo'] },
    { name: 'USV Strike',          domain: 'surface',    lat: 17.651, lng: 83.263, alt: 0.5, spd: 35,  hdg: 320, sensors: ['radar', 'eo'] },
    { name: 'UUV Torpedo',         domain: 'underwater', lat: 17.716, lng: 83.163, alt: -28, spd: 25,  hdg: 110, sensors: ['sonar'] },
    { name: 'Loitering Munition',  domain: 'air',        lat: 17.641, lng: 83.193, alt: 60,  spd: 110, hdg: 45,  sensors: ['radar', 'ir'] },
  ];
}

export default function LiveMode() {
  const [demoActive, setDemoActive] = useState(false);
  const [autoHandoff, setAutoHandoff] = useState(false);
  const [liveDemo, setLiveDemo] = useState(() => {
    try { return localStorage.getItem('bk_live_demo') === '1'; } catch { return false; }
  });
  const [state, setState] = useState({ sensors: [], raw_by_sensor: {}, fused_tracks: [] });
  const [now, setNow] = useState(Date.now());
  const targetsRef = useRef(makeDemoTargets());
  const demoTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const handedOffRef = useRef(new Set());
  const autoHandoffRef = useRef(false);
  useEffect(() => { autoHandoffRef.current = autoHandoff; }, [autoHandoff]);

  // Master Live Demo toggle: drives Demo Feed + Auto Hand-off here
  // and sets a localStorage flag so Auto Engage auto-arms when visited.
  useEffect(() => {
    try {
      if (liveDemo) localStorage.setItem('bk_live_demo', '1');
      else localStorage.removeItem('bk_live_demo');
    } catch { /* ignore */ }
    setDemoActive(liveDemo);
    setAutoHandoff(liveDemo);
  }, [liveDemo]);

  const HANDOFF_CONFIDENCE = 0.5;

  const handoff = async (track, opts = {}) => {
    try {
      await axios.post('/live/handoff', { track });
      handedOffRef.current.add(track.track_id);
      if (!opts.silent) toast.success(`${track.track_id} → handed off to Auto Engage`);
      else toast.message(`AUTO HAND-OFF: ${track.track_id} → Auto Engage`, { duration: 1800 });
    } catch (err) {
      if (!opts.silent) toast.error('Handoff failed');
    }
  };

  // Fetch live state every 1.2s (and run auto-handoff if armed)
  useEffect(() => {
    const fetchState = async () => {
      try {
        const res = await axios.get('/live/state');
        setState(res.data);
        setNow(Date.now());

        if (autoHandoffRef.current) {
          const candidates = (res.data.fused_tracks || []).filter(t =>
            t.confidence >= HANDOFF_CONFIDENCE && !handedOffRef.current.has(t.track_id)
          );
          for (const t of candidates) {
            handoff(t, { silent: true });
          }
        }
      } catch { /* silent */ }
    };
    fetchState();
    pollTimerRef.current = setInterval(fetchState, 1200);
    return () => clearInterval(pollTimerRef.current);
  }, []);

  // Demo simulator — pumps synthetic frames into the ingest endpoints
  useEffect(() => {
    if (!demoActive) {
      if (demoTimerRef.current) {
        clearInterval(demoTimerRef.current);
        demoTimerRef.current = null;
      }
      return;
    }

    const tick = async () => {
      // Advance each target slightly
      const advanced = targetsRef.current.map(tgt => {
        const headingRad = (tgt.hdg * Math.PI) / 180;
        const stepKm = (tgt.spd / 3600) * 0.9; // ~0.9s of motion
        const dLat = (Math.cos(headingRad) * stepKm) / 110.574;
        const dLng = (Math.sin(headingRad) * stepKm) / (111.32 * Math.cos((tgt.lat * Math.PI) / 180));
        return {
          ...tgt,
          lat: tgt.lat + dLat,
          lng: tgt.lng + dLng,
          // Slight heading wander to look natural
          hdg: (tgt.hdg + (Math.random() - 0.5) * 4 + 360) % 360,
        };
      });
      targetsRef.current = advanced;

      // Per-sensor batch with a bit of measurement noise
      const batches = { radar: [], sonar: [], eo: [], ir: [] };
      advanced.forEach(tgt => {
        tgt.sensors.forEach(stype => {
          const noise = (mag) => (Math.random() - 0.5) * mag;
          batches[stype].push({
            lat: tgt.lat + noise(0.0003),
            lng: tgt.lng + noise(0.0003),
            altitude: tgt.alt != null ? tgt.alt + noise(2) : null,
            speed: tgt.spd + noise(2),
            heading: (tgt.hdg + noise(3) + 360) % 360,
            signal_strength: 0.6 + Math.random() * 0.35,
            classification_hint: tgt.name,
            domain_hint: tgt.domain,
          });
        });
      });

      // Occasional false contact on radar to make it look real
      if (Math.random() < 0.2) {
        batches.radar.push({
          lat: 17.6868 + (Math.random() - 0.5) * 0.05,
          lng: 83.2185 + (Math.random() - 0.5) * 0.05,
          altitude: 200 + Math.random() * 200,
          speed: 100 + Math.random() * 200,
          heading: Math.random() * 360,
          signal_strength: 0.2 + Math.random() * 0.2,
          domain_hint: 'air',
        });
      }

      // POST to each sensor endpoint
      const sensorMap = { radar: 'RADAR-01', sonar: 'SONAR-01', eo: 'EO-01', ir: 'IR-01' };
      await Promise.all(Object.entries(batches).map(async ([stype, contacts]) => {
        if (!contacts.length) return;
        try {
          await axios.post(`/ingest/${stype}`, { sensor_id: sensorMap[stype], contacts });
        } catch { /* silent */ }
      }));
    };

    tick();
    demoTimerRef.current = setInterval(tick, 900);
    return () => {
      if (demoTimerRef.current) clearInterval(demoTimerRef.current);
    };
  }, [demoActive]);

  const sensorByType = (type) => state.sensors.find(s => s.sensor_type === type);

  // 2D plot config: convert lat/lng to canvas coords centered on ship at Visakhapatnam (17.6868, 83.2185)
  const PLOT_CENTER = { lat: 17.6868, lng: 83.2185 };
  const PLOT_KM = 8; // visible radius in km
  const plotXY = (lat, lng) => {
    const dLat = lat - PLOT_CENTER.lat;
    const dLng = lng - PLOT_CENTER.lng;
    const kmLat = dLat * 110.574;
    const kmLng = dLng * 111.32 * Math.cos((PLOT_CENTER.lat * Math.PI) / 180);
    return {
      x: 50 + (kmLng / PLOT_KM) * 50,
      y: 50 - (kmLat / PLOT_KM) * 50,
    };
  };

  const ageSec = (iso) => {
    if (!iso) return null;
    return Math.max(0, (now - new Date(iso).getTime()) / 1000);
  };

  return (
    <div className="h-full flex flex-col bg-zinc-950 overflow-hidden" data-testid="live-mode-page">
      {/* Header */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 p-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight uppercase flex items-center gap-2">
            <Activity size={22} className="text-emerald-400" />
            Live Mode
          </h1>
          <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1 font-mono">
            Real-world Sensor Ingest · Radar · Sonar · EO · IR · Modbus/LAN gateway
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLiveDemo(v => !v)}
            className={`px-5 py-2 rounded font-mono text-xs uppercase tracking-wider border-2 transition-all flex items-center gap-2 ${
              liveDemo
                ? 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400 shadow-[0_0_22px_rgba(217,70,239,0.5)]'
                : 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-fuchsia-500 hover:text-fuchsia-300'
            }`}
            data-testid="live-demo-master"
            title="One-click: arms Demo Feed + Auto Hand-off here, and Auto Engage on the next page"
          >
            <Cpu size={14} />
            {liveDemo ? '🟣 LIVE DEMO: ARMED' : 'LIVE DEMO'}
          </button>
          <Link
            to="/auto-engage"
            className="px-3 py-2 rounded font-mono text-xs uppercase border border-zinc-700 bg-zinc-800 text-zinc-400 hover:border-sky-600 hover:text-sky-300 flex items-center gap-1.5"
            data-testid="goto-auto-engage-from-live"
          >
            <ExternalLink size={12} /> Auto Engage
          </Link>
          <button
            onClick={() => {
              setAutoHandoff(v => {
                const next = !v;
                if (!next) handedOffRef.current = new Set();
                return next;
              });
            }}
            className={`px-4 py-2 rounded font-mono text-xs uppercase tracking-wider border-2 transition-all flex items-center gap-2 ${
              autoHandoff
                ? 'bg-red-500/20 text-red-300 border-red-500 shadow-[0_0_18px_rgba(239,68,68,0.35)]'
                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-red-600 hover:text-red-300'
            }`}
            data-testid="auto-handoff-toggle"
            title={`Tracks with confidence ≥ ${(HANDOFF_CONFIDENCE * 100).toFixed(0)}% are auto-promoted to engagement`}
          >
            <Target size={14} /> {autoHandoff ? 'AUTO HAND-OFF: ARMED' : 'AUTO HAND-OFF: OFF'}
          </button>
          <button
            onClick={() => setDemoActive(v => !v)}
            className={`px-4 py-2 rounded font-mono text-xs uppercase tracking-wider border-2 transition-all flex items-center gap-2 ${
              demoActive
                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500 shadow-[0_0_18px_rgba(16,185,129,0.35)]'
                : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-emerald-600 hover:text-emerald-300'
            }`}
            data-testid="demo-feed-toggle"
          >
            <Power size={14} /> {demoActive ? 'Demo Feed: ON' : 'Demo Feed: OFF'}
          </button>
        </div>
      </div>

      {/* Sensor tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-4 border-b border-zinc-800">
        {SENSORS.map(s => {
          const Icon = s.icon;
          const health = sensorByType(s.type);
          const status = health?.status || 'offline';
          const lastAge = ageSec(health?.last_frame_at);
          const recent = state.raw_by_sensor[s.id] || [];
          return (
            <div
              key={s.id}
              className={`bg-zinc-950/80 border rounded-sm p-3 transition-colors ${
                status === 'online' ? 'border-emerald-700/40' : 'border-zinc-800'
              }`}
              data-testid={`sensor-tile-${s.type}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={18} style={{ color: s.color }} />
                  <div>
                    <div className="text-sm font-bold uppercase">{s.label}</div>
                    <div className="text-[10px] font-mono text-zinc-500">{s.id}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    status === 'online' ? 'bg-emerald-500' : 'bg-zinc-600'
                  }`} />
                  <span className={`text-[10px] font-mono uppercase ${
                    status === 'online' ? 'text-emerald-400' : 'text-zinc-500'
                  }`}>{status}</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-1 text-[10px] font-mono mb-2">
                <div>
                  <div className="text-zinc-600 uppercase">Frames</div>
                  <div className="text-zinc-300">{health?.frames_total || 0}</div>
                </div>
                <div>
                  <div className="text-zinc-600 uppercase">Contacts</div>
                  <div className="text-zinc-300">{health?.contacts_total || 0}</div>
                </div>
                <div>
                  <div className="text-zinc-600 uppercase">Last</div>
                  <div className="text-zinc-300">{lastAge != null ? `${lastAge.toFixed(1)}s` : '—'}</div>
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase text-zinc-600 mb-1">Recent contacts</div>
              <div className="space-y-0.5 max-h-24 overflow-y-auto">
                {recent.length === 0 ? (
                  <div className="text-[10px] text-zinc-600 italic">no recent contacts</div>
                ) : recent.slice(0, 5).map(c => (
                  <div key={c.id} className="text-[10px] font-mono text-zinc-400 truncate">
                    {c.classification_hint ? `${c.classification_hint} · ` : ''}
                    {c.lat.toFixed(4)},{c.lng.toFixed(4)}
                    {c.altitude != null && ` · alt ${c.altitude.toFixed(0)}m`}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Body: plot + fused tracks */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_22rem] gap-0 overflow-hidden">
        {/* 2D plot */}
        <div className="p-4 overflow-hidden flex flex-col">
          <h2 className="text-sm font-bold uppercase tracking-wider mb-2 flex items-center gap-2">
            <Crosshair size={16} className="text-emerald-400" /> Tactical Plot
            <span className="text-[10px] text-zinc-600 font-mono">±{PLOT_KM}km radius</span>
          </h2>
          <div className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm relative overflow-hidden" data-testid="tactical-plot">
            {/* Range rings */}
            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
              {[0.25, 0.5, 0.75, 1].map((f) => (
                <circle key={f} cx="50" cy="50" r={50 * f} fill="none" stroke="#1e293b" strokeWidth="0.15" />
              ))}
              {/* Cross */}
              <line x1="0" y1="50" x2="100" y2="50" stroke="#1e293b" strokeWidth="0.1" />
              <line x1="50" y1="0" x2="50" y2="100" stroke="#1e293b" strokeWidth="0.1" />
              {/* Ship marker */}
              <g>
                <circle cx="50" cy="50" r="0.9" fill="#fbbf24" />
                <circle cx="50" cy="50" r="2.0" fill="none" stroke="#fbbf24" strokeWidth="0.2" opacity="0.5" />
              </g>
              {/* Raw contacts (subtle) */}
              {Object.entries(state.raw_by_sensor).map(([sid, contacts]) =>
                contacts.slice(0, 12).map(c => {
                  const sensorMeta = SENSORS.find(s => s.id === sid);
                  const { x, y } = plotXY(c.lat, c.lng);
                  return (
                    <circle
                      key={c.id}
                      cx={x} cy={y} r="0.35"
                      fill={sensorMeta?.color || '#64748b'}
                      opacity="0.35"
                    />
                  );
                })
              )}
              {/* Fused tracks */}
              {state.fused_tracks.map(t => {
                const { x, y } = plotXY(t.lat, t.lng);
                const isAir = t.domain === 'air';
                const isSurf = t.domain === 'surface';
                return (
                  <g key={t.track_id}>
                    {isAir && (
                      <polygon
                        points={`${x},${y - 2.6} ${x + 2.2},${y + 1.6} ${x - 2.2},${y + 1.6}`}
                        fill="#38bdf8" opacity="0.95"
                        stroke="#0ea5e9" strokeWidth="0.25"
                      />
                    )}
                    {isSurf && (
                      <rect x={x - 2.2} y={y - 1.4} width="4.4" height="2.8" fill="#34d399" opacity="0.95"
                        stroke="#10b981" strokeWidth="0.25" />
                    )}
                    {!isAir && !isSurf && (
                      <circle cx={x} cy={y} r="2.0" fill="#60a5fa" opacity="0.95"
                        stroke="#3b82f6" strokeWidth="0.25" />
                    )}
                    <circle cx={x} cy={y} r={3.0 + t.confidence * 1.5} fill="none" stroke="#f59e0b" strokeWidth="0.2" opacity="0.7" />
                    <text x={x + 2.8} y={y + 0.6} fontSize="2.2" fill="#cbd5e1" fontFamily="monospace">{t.track_id.replace('L-', '')}</text>
                  </g>
                );
              })}
            </svg>
            {state.fused_tracks.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-zinc-600 font-mono text-xs">
                  {demoActive ? 'Awaiting fused tracks...' : 'Toggle Demo Feed or POST to /api/ingest/* to see tracks'}
                </p>
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-zinc-500">
            <span className="flex items-center gap-1"><span style={{ color: '#fbbf24' }}>●</span> Ship</span>
            <span className="flex items-center gap-1 text-sky-400">▲ Air</span>
            <span className="flex items-center gap-1 text-emerald-400">■ Surface</span>
            <span className="flex items-center gap-1 text-blue-400">● Underwater</span>
            <span className="flex items-center gap-1 text-amber-400">○ Confidence ring</span>
          </div>
        </div>

        {/* Fused tracks panel */}
        <div className="border-l border-zinc-800 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-zinc-800 flex items-center gap-2">
            <Cpu size={14} className="text-emerald-400" />
            <span className="text-xs font-bold uppercase tracking-wider">Fused Tracks</span>
            <span className="ml-auto text-[10px] text-zinc-600 font-mono">{state.fused_tracks.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="fused-tracks-list">
            {state.fused_tracks.length === 0 ? (
              <div className="text-zinc-600 font-mono text-xs py-12 text-center">
                {demoActive ? 'Sensors warming up...' : 'No live tracks. Enable Demo Feed or wire a sensor adapter.'}
              </div>
            ) : (
              state.fused_tracks.map(t => {
                const domainLabel = { air: 'AIR ▲', surface: 'SURF ■', underwater: 'SUB ●' }[t.domain] || t.domain;
                const domainCol = { air: 'text-sky-400 border-sky-500/40', surface: 'text-emerald-400 border-emerald-500/40', underwater: 'text-blue-400 border-blue-500/40' }[t.domain];
                return (
                  <div key={t.track_id} className="bg-zinc-950/80 border border-zinc-800 rounded-sm p-2.5" data-testid={`fused-track-${t.track_id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-mono text-white">{t.track_id}</span>
                        <span className={`text-[9px] px-1 rounded border ${domainCol}`}>{domainLabel}</span>
                        {handedOffRef.current.has(t.track_id) && (
                          <span className="text-[9px] px-1 rounded border bg-red-500/15 text-red-300 border-red-500/40" title="Handed off to Auto Engage">⚔ ENGAGED</span>
                        )}
                      </div>
                      <span className="text-[10px] font-mono text-amber-400">conf {(t.confidence * 100).toFixed(0)}%</span>
                    </div>
                    {t.classification_hint && (
                      <div className="text-[11px] text-zinc-300 truncate">{t.classification_hint}</div>
                    )}
                    <div className="grid grid-cols-2 gap-1 text-[10px] font-mono text-zinc-500 mt-1">
                      <div>{t.lat.toFixed(4)}, {t.lng.toFixed(4)}</div>
                      {t.altitude != null && <div>alt {t.altitude.toFixed(0)}m</div>}
                      {t.speed != null && <div>{t.speed.toFixed(0)} km/h</div>}
                      {t.heading != null && <div>hdg {t.heading.toFixed(0)}°</div>}
                    </div>
                    <div className="mt-1 text-[10px] font-mono text-zinc-600">
                      sensors: {t.sensor_types.join(' · ')}
                    </div>
                    <button
                      onClick={() => handoff(t)}
                      className="mt-2 w-full flex items-center justify-center gap-1 py-1 rounded text-[10px] font-mono uppercase border border-zinc-700 bg-zinc-800 text-zinc-300 hover:border-red-500 hover:text-red-300 transition-all"
                      data-testid={`handoff-${t.track_id}`}
                    >
                      <Target size={10} /> Engage (hand off)
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
